import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@capacitor/inappbrowser';
import { ILogger, LoggerService } from '@timechef/core/logging';

/**
 * Modes d'ouverture du navigateur
 */
export type BrowserOpenMode =
  | 'webview' // WebView intégrée dans l'app (contrôle total, monitoring possible)
  | 'system-browser' // Navigateur système natif (Custom Tabs/SFSafariViewController)
  | 'external-browser'; // Navigateur externe par défaut du système

/**
 * Configuration options for redirecting to a URL with callback handling.
 * Typically used for OAuth/authentication flows.
 */
export type InAppBrowserRedirectOptions = {
  /** The external URL to navigate to */
  url: string;
  /** The URL path pattern to watch for as a callback trigger */
  callbackPath: string;
  /** Callback function executed when the callback URL is detected, receives the full URL */
  onCallback?: (callbackUrl: string) => void;
  /** Callback function executed when the callback URL is detected, receives only the path portion */
  onCallbackPath?: (path: string) => void;
  /** Override platform detection (defaults to Capacitor.isNativePlatform()) */
  isNativePlatform?: boolean;
  /** Whether to automatically close the browser when callback is detected (defaults to true) */
  closeOnCallback?: boolean;
  /**
   * Browser opening mode (defaults to 'webview'):
   * - 'webview': WebView intégrée dans l'app (contrôle total, monitoring possible)
   * - 'system-browser': Navigateur système natif (Custom Tabs/SFSafariViewController)
   * - 'external-browser': Navigateur externe par défaut du système
   */
  openMode?: BrowserOpenMode;
};

/**
 * Service for opening external URLs in an in-app browser.
 * Provides wrapper functionality around @capacitor/inappbrowser plugin with
 * special support for callback-based authentication flows.
 */
@Injectable({ providedIn: 'root' })
export class InAppBrowserService {
  private logger: ILogger;
  private isBrowserOpen = false;

  constructor(loggerService: LoggerService) {
    this.logger = loggerService.getLogger('InAppBrowserService');
  }

  /**
   * Opens an external URL in the in-app browser with predefined configuration.
   * @param url - The URL to open
   * @param openMode - Browser opening mode (defaults to 'webview'):
   *   - 'webview': WebView intégrée dans l'app (contrôle total, monitoring possible)
   *   - 'system-browser': Navigateur système natif (Custom Tabs/SFSafariViewController)
   *   - 'external-browser': Navigateur externe par défaut du système
   * @returns Promise that resolves when the browser is opened
   */
  async navigateToUrl(url: string, openMode: BrowserOpenMode = 'webview'): Promise<void> {
    this.logger.debug('Opening URL in browser:', { url, openMode });

    if (openMode === 'system-browser') {
      this.isBrowserOpen = true;
      await InAppBrowser.openInSystemBrowser({
        url,
        options: {
          android: {
            showTitle: true,
            hideToolbarOnScroll: false,
            viewStyle: 1, // FULL_SCREEN
            startAnimation: 3, // SLIDE_OUT_RIGHT
            exitAnimation: 2, // SLIDE_IN_LEFT
          },
          iOS: {
            closeButtonText: 0, // CLOSE
            viewStyle: 2, // FULL_SCREEN
            animationEffect: 2, // COVER_VERTICAL
            enableBarsCollapsing: false,
            enableReadersMode: false,
          },
        },
      });
    } else if (openMode === 'external-browser') {
      await InAppBrowser.openInExternalBrowser({ url });
    } else {
      this.isBrowserOpen = true;
      await InAppBrowser.openInWebView({
        url,
        options: {
          showURL: false,
          showToolbar: false,
          clearCache: false,
          clearSessionCache: false,
          mediaPlaybackRequiresUserAction: false,
          closeButtonText: 'Close',
          toolbarPosition: 1, // BOTTOM
          showNavigationButtons: false,
          leftToRight: false,
          android: {
            allowZoom: false,
            hardwareBack: true,
            pauseMedia: false,
          },
          iOS: {
            allowOverScroll: false,
            enableViewportScale: false,
            allowInLineMediaPlayback: true,
            surpressIncrementalRendering: false,
            viewStyle: 2, // FULL_SCREEN
            animationEffect: 2, // COVER_VERTICAL
            allowsBackForwardNavigationGestures: true,
          },
        },
      });
    }
  }

  /**
   * Closes the currently open in-app browser if any.
   * Useful when a deep link is triggered and the browser should be dismissed.
   */
  async closeCurrentBrowser(): Promise<void> {
    if (!this.isBrowserOpen) return;
    this.logger.debug('Closing current in-app browser');
    InAppBrowser.removeAllListeners();
    await InAppBrowser.close();
    this.isBrowserOpen = false;
  }

  /**
   * Navigates to an external URL and monitors for a callback URL pattern.
   * On web platforms, performs a standard redirect. On native platforms, opens
   * the URL in an in-app browser and watches for the callback path.
   *
   * @param options - Configuration object for the redirect with callback
   * @example
   * ```typescript
   * await inAppBrowserService.redirectWithCallback({
   *   url: 'https://auth.provider.com/authorize?...',
   *   callbackPath: '/auth/callback',
   *   onCallbackPath: (path) => {
   *     // Process the callback path
   *   }
   * });
   * ```
   */
  async redirectWithCallback(options: InAppBrowserRedirectOptions) {
    const { url, callbackPath, onCallback, closeOnCallback, openMode = 'webview' } = options;
    this.logger.debug('Redirect with callback requested:', { url, callbackPath, openMode });

    if (!url) {
      this.logger.debug('No URL provided, aborting');
      return;
    }

    // Determine if running on a native platform (iOS/Android)
    const isNativePlatform = options.isNativePlatform ?? Capacitor.isNativePlatform();
    this.logger.debug('Platform detection:', { isNativePlatform });

    // On web platforms, perform a standard redirect
    if (!isNativePlatform) {
      this.logger.debug('Web platform detected, performing standard redirect');
      window.location.href = url;
      return;
    }

    this.logger.debug('Native platform detected, opening in-app browser');

    if (openMode === 'system-browser') {
      this.logger.debug('Opening in system browser (callback monitoring not necessary with Universal Links/Deep Links');
      await this.navigateToUrl(url, openMode);
      return;
    }

    if (openMode === 'external-browser') {
      this.logger.debug('Opening in external browser (callback monitoring not available)');
      await this.navigateToUrl(url, openMode);
      return;
    }

    // Listener de fermeture manuelle par l'utilisateur
    await InAppBrowser.addListener('browserClosed', async () => {
      this.logger.debug('Browser closed by user, cleaning up listeners');
      this.isBrowserOpen = false;
      InAppBrowser.removeAllListeners();
    });

    await InAppBrowser.addListener('browserPageNavigationCompleted', async (data) => {
      const eventUrl = data.url || '';
      this.logger.debug('URL navigation detected:', eventUrl);

      if (!eventUrl.includes(callbackPath)) {
        this.logger.debug('URL does not match callback path, continuing to monitor');
        return;
      }

      this.logger.debug('Callback path detected in URL, processing callback');

      if (closeOnCallback ?? true) {
        this.logger.debug('Closing in-app browser');
        await InAppBrowser.close();
        this.isBrowserOpen = false;
      }

      InAppBrowser.removeAllListeners();

      if (options.onCallbackPath) {
        const path = this.getPathFromCallbackUrl(eventUrl);
        this.logger.debug('Executing onCallbackPath callback with path:', path);
        options.onCallbackPath(path);
      }

      if (onCallback) {
        this.logger.debug('Executing onCallback with full URL');
        onCallback(eventUrl);
      }
    });

    // Open the URL in webview mode with the listener configured
    await this.navigateToUrl(url, 'webview');
  }

  /**
   * Extracts the path portion from a full callback URL (removes the origin).
   * @param callbackUrl - Full URL string
   * @returns The path portion of the URL (everything after the origin)
   * @example
   * ```typescript
   * getPathFromCallbackUrl('https://example.com/auth/callback?code=123')
   * // Returns: '/auth/callback?code=123'
   * ```
   */
  getPathFromCallbackUrl(callbackUrl: string): string {
    const urlObj = new URL(callbackUrl);
    const path = callbackUrl.substring(urlObj.origin.length);
    this.logger.debug('Extracted path from callback URL:', { callbackUrl, path });
    return path;
  }
}
